import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Search, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar,
  Package,
  Gavel,
  ShieldAlert,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  X,
  Eye,
  Ban,
  CheckCircle,
  Crown
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [blockedOnly, setBlockedOnly] = useState(false);
  const [pagination, setPagination] = useState({ skip: 0, limit: 20, total: 0 });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [userDetails, setUserDetails] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    fetchUsers();
  }, [pagination.skip, blockedOnly]);

  // Clear selection when filters/page change
  useEffect(() => { setSelectedIds(new Set()); }, [pagination.skip, blockedOnly, search]);

  const toggleSelect = (userId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const eligibleUserIdsOnPage = users.filter(u => !u.is_admin).map(u => u.id);
  const allSelectedOnPage = eligibleUserIdsOnPage.length > 0 && eligibleUserIdsOnPage.every(id => selectedIds.has(id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelectedOnPage) {
        eligibleUserIdsOnPage.forEach((id) => next.delete(id));
      } else {
        eligibleUserIdsOnPage.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const bulkAction = async (action) => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    let reason = 'Bulk admin action';
    if (action === 'block') {
      reason = window.prompt(`Block ${ids.length} user(s)? Enter a reason:`, 'Policy violation') || '';
      if (!reason.trim()) return;
    } else if (!window.confirm(`Unblock ${ids.length} user(s)?`)) {
      return;
    }
    setActionLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/users/bulk`, {
        user_ids: ids, action, reason,
      });
      toast.success(res.data.message || 'Bulk action complete');
      setSelectedIds(new Set());
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk action failed');
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setPagination(prev => ({ ...prev, skip: 0 }));
      fetchUsers();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: pagination.skip,
        limit: pagination.limit,
        blocked_only: blockedOnly
      });
      if (search) params.append('search', search);

      const response = await axios.get(`${API_URL}/api/admin/users?${params}`);
      setUsers(response.data.users);
      setPagination(prev => ({ ...prev, total: response.data.total }));
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users/${userId}`);
      setUserDetails(response.data);
    } catch (error) {
      toast.error('Failed to fetch user details');
    }
  };

  const handleViewUser = async (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
    await fetchUserDetails(user.id);
  };

  const handleBlockUser = (user) => {
    setSelectedUser(user);
    setBlockReason('');
    setShowBlockModal(true);
  };

  const confirmBlockUser = async () => {
    if (!blockReason.trim()) {
      toast.error('Please provide a reason for blocking');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/users/${selectedUser.id}/block`, {
        reason: blockReason
      });
      toast.success('User blocked successfully');
      setShowBlockModal(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnblockUser = async (user) => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/users/${user.id}/unblock`);
      toast.success('User unblocked successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unblock user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMakeAdmin = async (user) => {
    if (!confirm(`Make ${user.name} an admin? This will give them full admin access.`)) return;
    
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/users/${user.id}/make-admin`);
      toast.success('User promoted to admin');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to make admin');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveAdmin = async (user) => {
    if (!confirm(`Remove admin privileges from ${user.name}?`)) return;
    
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/users/${user.id}/remove-admin`);
      toast.success('Admin privileges removed');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove admin');
    } finally {
      setActionLoading(false);
    }
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          User Management
        </h1>
        <p className="text-slate-400">View and manage all platform users</p>
      </div>

      {/* Filters */}
      <div className="bg-slate-800 rounded-2xl p-4 mb-6 border border-slate-700 flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, phone, or city..."
            className="w-full pl-12 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            data-testid="user-search-input"
          />
        </div>
        <label className="flex items-center gap-2 cursor-pointer px-4 py-3 bg-slate-700 rounded-xl">
          <input
            type="checkbox"
            checked={blockedOnly}
            onChange={(e) => setBlockedOnly(e.target.checked)}
            className="w-4 h-4 rounded border-slate-500 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-slate-300">Blocked users only</span>
        </label>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="bg-indigo-600/20 border border-indigo-500/40 rounded-xl px-4 py-3 mb-4 flex items-center justify-between" data-testid="bulk-action-bar">
          <p className="text-sm text-indigo-200">
            <strong className="text-white">{selectedIds.size}</strong> user{selectedIds.size > 1 ? 's' : ''} selected
            <span className="ml-2 text-indigo-300">(admins are ignored)</span>
          </p>
          <div className="flex gap-2">
            <Button
              onClick={() => bulkAction('block')}
              disabled={actionLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="bulk-block-btn"
            >
              <Ban className="w-4 h-4 mr-2" /> Block Selected
            </Button>
            <Button
              onClick={() => bulkAction('unblock')}
              disabled={actionLoading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="bulk-unblock-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" /> Unblock Selected
            </Button>
            <Button
              variant="outline"
              onClick={() => setSelectedIds(new Set())}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
        {loading ? (
          <div className="p-8 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            <User className="w-12 h-12 mx-auto mb-2 text-slate-600" />
            <p>No users found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="px-4 py-4 text-left">
                    <input
                      type="checkbox"
                      checked={allSelectedOnPage}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded accent-indigo-600"
                      data-testid="select-all-users"
                      title="Select all eligible users on this page"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">User</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Contact</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Location</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Activity</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Status</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-slate-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {users.map((user) => (
                  <tr 
                    key={user.id} 
                    className="hover:bg-slate-700/30 transition-colors"
                    data-testid={`user-row-${user.id}`}
                  >
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(user.id)}
                        onChange={() => toggleSelect(user.id)}
                        disabled={user.is_admin}
                        className="w-4 h-4 rounded accent-indigo-600 disabled:opacity-30"
                        data-testid={`select-user-${user.id}`}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold">{user.name?.[0]?.toUpperCase()}</span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-white font-medium">{user.name}</p>
                            {user.is_admin && (
                              <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full flex items-center gap-1">
                                <Crown className="w-3 h-3" />
                                Admin
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-400">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="flex items-center gap-2 text-slate-300">
                          <Mail className="w-4 h-4 text-slate-500" />
                          {user.email}
                        </div>
                        {user.phone_number && (
                          <div className="flex items-center gap-2 text-slate-400 mt-1">
                            <Phone className="w-4 h-4 text-slate-500" />
                            {user.country_code} {user.phone_number}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-slate-300">
                        <MapPin className="w-4 h-4 text-slate-500" />
                        {[user.city, user.state, user.country].filter(Boolean).join(', ') || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1 text-slate-300">
                          <Package className="w-4 h-4 text-emerald-500" />
                          {user.listings_count || 0}
                        </span>
                        <span className="flex items-center gap-1 text-slate-300">
                          <Gavel className="w-4 h-4 text-amber-500" />
                          {user.bids_count || 0}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {user.is_blocked ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-medium">
                          <ShieldAlert className="w-3 h-3" />
                          Blocked
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-xs font-medium">
                          <ShieldCheck className="w-3 h-3" />
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewUser(user)}
                          className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                          title="View Details"
                          data-testid={`view-user-${user.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {user.is_blocked ? (
                          <button
                            onClick={() => handleUnblockUser(user)}
                            className="p-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 rounded-lg transition-colors"
                            title="Unblock User"
                            data-testid={`unblock-user-${user.id}`}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleBlockUser(user)}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                            title="Block User"
                            disabled={user.is_admin}
                            data-testid={`block-user-${user.id}`}
                          >
                            <Ban className="w-4 h-4" />
                          </button>
                        )}
                        {!user.is_admin ? (
                          <button
                            onClick={() => handleMakeAdmin(user)}
                            className="p-2 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 rounded-lg transition-colors"
                            title="Make Admin"
                            data-testid={`make-admin-${user.id}`}
                          >
                            <Crown className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleRemoveAdmin(user)}
                            className="p-2 text-slate-400 hover:text-slate-300 hover:bg-slate-600 rounded-lg transition-colors"
                            title="Remove Admin"
                            data-testid={`remove-admin-${user.id}`}
                          >
                            <Crown className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between">
          <p className="text-sm text-slate-400">
            Showing {pagination.skip + 1} to {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total} users
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
              disabled={pagination.skip === 0}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-slate-400 text-sm">
              Page {currentPage} of {totalPages || 1}
            </span>
            <button
              onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
              disabled={pagination.skip + pagination.limit >= pagination.total}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* User Details Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent className="max-w-2xl bg-slate-800 border-slate-700 text-white" data-testid="user-details-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">{selectedUser?.name?.[0]?.toUpperCase()}</span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  {selectedUser?.name}
                  {selectedUser?.is_admin && (
                    <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">Admin</span>
                  )}
                  {selectedUser?.is_blocked && (
                    <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">Blocked</span>
                  )}
                </div>
                <p className="text-sm text-slate-400">{selectedUser?.email}</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {userDetails ? (
            <div className="mt-4 space-y-6">
              {/* User Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-700/50 rounded-xl p-4">
                  <p className="text-slate-400 text-sm mb-1">Phone</p>
                  <p className="text-white">{userDetails.user.country_code} {userDetails.user.phone_number || 'N/A'}</p>
                </div>
                <div className="bg-slate-700/50 rounded-xl p-4">
                  <p className="text-slate-400 text-sm mb-1">Location</p>
                  <p className="text-white">
                    {[userDetails.user.city, userDetails.user.state, userDetails.user.country].filter(Boolean).join(', ') || 'N/A'}
                  </p>
                </div>
                <div className="bg-slate-700/50 rounded-xl p-4">
                  <p className="text-slate-400 text-sm mb-1">Rating</p>
                  <p className="text-white">{userDetails.user.rating?.toFixed(1) || '0.0'} ({userDetails.user.reviews_count || 0} reviews)</p>
                </div>
                <div className="bg-slate-700/50 rounded-xl p-4">
                  <p className="text-slate-400 text-sm mb-1">Joined</p>
                  <p className="text-white">{new Date(userDetails.user.created_at).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-400">{userDetails.stats?.total_listings || 0}</p>
                  <p className="text-sm text-slate-400">Listings</p>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-amber-400">{userDetails.stats?.total_bids || 0}</p>
                  <p className="text-sm text-slate-400">Bids</p>
                </div>
                <div className="bg-violet-500/10 border border-violet-500/30 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-violet-400">{userDetails.stats?.total_trades || 0}</p>
                  <p className="text-sm text-slate-400">Trades</p>
                </div>
              </div>

              {/* Block reason if blocked */}
              {userDetails.user.is_blocked && userDetails.user.blocked_reason && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                  <p className="text-red-400 font-medium mb-1">Block Reason:</p>
                  <p className="text-slate-300">{userDetails.user.blocked_reason}</p>
                  {userDetails.user.blocked_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      Blocked on {new Date(userDetails.user.blocked_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}

              {/* Recent Listings */}
              {userDetails.recent_listings?.length > 0 && (
                <div>
                  <h4 className="text-white font-medium mb-2">Recent Listings</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {userDetails.recent_listings.map((listing) => (
                      <div key={listing.id} className="bg-slate-700/30 rounded-lg p-3 flex justify-between items-center">
                        <div>
                          <p className="text-white text-sm">{listing.title}</p>
                          <p className="text-xs text-slate-400">{listing.category} - {listing.status}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Block User Modal */}
      <Dialog open={showBlockModal} onOpenChange={setShowBlockModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white" data-testid="block-user-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <ShieldAlert className="w-5 h-5" />
              Block User
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <p className="text-slate-300">
              Are you sure you want to block <strong>{selectedUser?.name}</strong>? 
              They will not be able to access their account.
            </p>
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Reason for blocking *</label>
              <textarea
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="Provide a reason for blocking this user..."
                className="w-full bg-slate-700 border border-slate-600 rounded-xl p-3 text-white placeholder-slate-500 focus:ring-2 focus:ring-red-500"
                rows={3}
                data-testid="block-reason-input"
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowBlockModal(false)}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmBlockUser}
                disabled={actionLoading || !blockReason.trim()}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-block-btn"
              >
                {actionLoading ? 'Blocking...' : 'Block User'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
